/**
 * A 5x7 bitmap font, authored in the same style as the sprite art: one
 * template literal per glyph, `#` for ink and `.` for paper.
 *
 * The HUD is the only text in the game, so this is deliberately the smallest
 * set of characters that can spell a HUD line plus the punctuation the editor
 * wants. Glyphs are 5 wide with 1px of letterspacing, so a string advances
 * 6px per character at scale 1.
 */

/** Glyph cell width in pixels. */
export const GLYPH_W = 5
/** Glyph cell height in pixels. */
export const GLYPH_H = 7
/** Horizontal distance between two glyph origins, in unscaled pixels. */
export const GLYPH_ADVANCE = GLYPH_W + 1

const GLYPH_SOURCE: Record<string, string> = {
  '0': `
.###.
#...#
#..##
#.#.#
##..#
#...#
.###.`,
  '1': `
..#..
.##..
..#..
..#..
..#..
..#..
.###.`,
  '2': `
.###.
#...#
....#
...#.
..#..
.#...
#####`,
  '3': `
.###.
#...#
....#
..##.
....#
#...#
.###.`,
  '4': `
...#.
..##.
.#.#.
#..#.
#####
...#.
...#.`,
  '5': `
#####
#....
####.
....#
....#
#...#
.###.`,
  '6': `
..##.
.#...
#....
####.
#...#
#...#
.###.`,
  '7': `
#####
....#
...#.
..#..
.#...
.#...
.#...`,
  '8': `
.###.
#...#
#...#
.###.
#...#
#...#
.###.`,
  '9': `
.###.
#...#
#...#
.####
....#
...#.
.##..`,
  A: `
.###.
#...#
#...#
#####
#...#
#...#
#...#`,
  B: `
####.
#...#
#...#
####.
#...#
#...#
####.`,
  C: `
.###.
#...#
#....
#....
#....
#...#
.###.`,
  D: `
####.
#...#
#...#
#...#
#...#
#...#
####.`,
  E: `
#####
#....
#....
####.
#....
#....
#####`,
  F: `
#####
#....
#....
####.
#....
#....
#....`,
  G: `
.###.
#...#
#....
#..##
#...#
#...#
.###.`,
  H: `
#...#
#...#
#...#
#####
#...#
#...#
#...#`,
  I: `
.###.
..#..
..#..
..#..
..#..
..#..
.###.`,
  J: `
..###
...#.
...#.
...#.
...#.
#..#.
.##..`,
  K: `
#...#
#..#.
#.#..
##...
#.#..
#..#.
#...#`,
  L: `
#....
#....
#....
#....
#....
#....
#####`,
  M: `
#...#
##.##
#.#.#
#.#.#
#...#
#...#
#...#`,
  N: `
#...#
#...#
##..#
#.#.#
#..##
#...#
#...#`,
  O: `
.###.
#...#
#...#
#...#
#...#
#...#
.###.`,
  P: `
####.
#...#
#...#
####.
#....
#....
#....`,
  Q: `
.###.
#...#
#...#
#...#
#.#.#
#..#.
.##.#`,
  R: `
####.
#...#
#...#
####.
#.#..
#..#.
#...#`,
  S: `
.####
#....
#....
.###.
....#
....#
####.`,
  T: `
#####
..#..
..#..
..#..
..#..
..#..
..#..`,
  U: `
#...#
#...#
#...#
#...#
#...#
#...#
.###.`,
  V: `
#...#
#...#
#...#
#...#
#...#
.#.#.
..#..`,
  W: `
#...#
#...#
#...#
#.#.#
#.#.#
##.##
#...#`,
  X: `
#...#
#...#
.#.#.
..#..
.#.#.
#...#
#...#`,
  Y: `
#...#
#...#
.#.#.
..#..
..#..
..#..
..#..`,
  Z: `
#####
....#
...#.
..#..
.#...
#....
#####`,
  // Lowercase x is the "times" symbol (the lives/coins counter).
  x: `
.....
.....
#...#
.#.#.
..#..
.#.#.
#...#`,
  '-': `
.....
.....
.....
#####
.....
.....
.....`,
  '+': `
.....
..#..
..#..
#####
..#..
..#..
.....`,
  '.': `
.....
.....
.....
.....
.....
.##..
.##..`,
  '!': `
..#..
..#..
..#..
..#..
..#..
.....
..#..`,
  '?': `
.###.
#...#
....#
..##.
..#..
.....
..#..`,
  '/': `
....#
....#
...#.
..#..
.#...
#....
#....`,
  ':': `
.....
.##..
.##..
.....
.##..
.##..
.....`,
  ',': `
.....
.....
.....
.....
.....
.##..
.#...`,
  // Straight apostrophe, for "TAVI'S WORKSHOP" and possessive labels. The
  // stroke sits in the first column so it hugs the letter before it.
  "'": `
.#...
.#...
.#...
.....
.....
.....
.....`,
  ' ': `
.....
.....
.....
.....
.....
.....
.....`,
}

function parseGlyph(art: string): readonly string[] {
  return art
    .split('\n')
    .map((row) => row.replace(/\s+$/, ''))
    .filter((row) => row.length > 0)
}

/** Glyph bitmaps keyed by the character they draw. */
export const FONT: Readonly<Record<string, readonly string[]>> = (() => {
  const out: Record<string, readonly string[]> = {}
  for (const [ch, art] of Object.entries(GLYPH_SOURCE)) {
    const rows = parseGlyph(art)
    if (rows.length !== GLYPH_H) {
      throw new Error(
        `font: glyph "${ch}" has ${rows.length} rows, expected ${GLYPH_H}`,
      )
    }
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (row !== undefined && row.length !== GLYPH_W) {
        throw new Error(
          `font: glyph "${ch}" row ${i} is ${row.length} wide, expected ${GLYPH_W}`,
        )
      }
    }
    out[ch] = rows
  }
  return out
})()

export interface TextOptions {
  /** Integer pixel multiplier. */
  scale?: number
  /** Outline colour, or null for none. Defaults to black. */
  outline?: string | null
}

/** Rendered width of `text` in pixels, excluding the trailing letterspace. */
export function textWidth(text: string, scale = 1): number {
  const s = Math.max(1, Math.floor(scale))
  if (text.length === 0) return 0
  return text.length * GLYPH_ADVANCE * s - s
}

function stamp(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  scale: number,
  colour: string,
): void {
  ctx.fillStyle = colour
  let cx = x
  for (const ch of text) {
    const glyph = FONT[ch]
    if (glyph !== undefined) {
      for (let row = 0; row < glyph.length; row++) {
        const bits = glyph[row]
        if (bits === undefined) continue
        for (let col = 0; col < bits.length; col++) {
          if (bits[col] !== '#') continue
          ctx.fillRect(cx + col * scale, y + row * scale, scale, scale)
        }
      }
    }
    cx += GLYPH_ADVANCE * scale
  }
}

/**
 * Draw `text` with its top-left corner at (x, y). Both must be integers: this
 * is pixel art, and a fractional origin makes every glyph blur by a half pixel.
 */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  colour: string,
  opts: TextOptions = {},
): void {
  const scale = Math.max(1, Math.floor(opts.scale ?? 1))
  const outline = opts.outline === undefined ? '#000000' : opts.outline
  const upper = text.toUpperCase()
  const ox = Math.floor(x)
  const oy = Math.floor(y)
  const px = Math.max(1, scale)

  ctx.save()
  if (outline !== null && outline.length > 0) {
    stamp(ctx, upper, ox - px, oy, scale, outline)
    stamp(ctx, upper, ox + px, oy, scale, outline)
    stamp(ctx, upper, ox, oy - px, scale, outline)
    stamp(ctx, upper, ox, oy + px, scale, outline)
  }
  stamp(ctx, upper, ox, oy, scale, colour)
  ctx.restore()
}
